/**
 * AnimatedUpdate React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2020 contexagon GmbH
 */


import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion'


function applyTransition(animate, transition) {
    if (!transition) return animate;
    else return {...animate, transition: transition};
}


/**
 * AnimatedUpdate animates its children each time the tag property changes
 * (which should be key-like unique for an item).
 * Animations are defined by the animate property (given as array like
 * [{opacity: 0}, {opacity: 1}, {opacity: 0}]) and optionally by a transition property
 * (given as array like [{duration: 2}, {duration: 3}]).
 * For both properties the last item defines the exit animation
 * and can be omitted in case it equals the first item.
 * In case the order property is set, start and end animations
 * are reversed, if the new order value is smaller
 * (to indicate direction like cover flow animations do).
 *
 * @author Jens Gruschel
 */
export default class AnimatedUpdate extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            history: [],
            current: {
                counter: 0,
                children: props.children,
                appearingReverse: false,
                veryFirst: true
            }
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.tag !== prevProps.tag) {
            const counter = this.state.current.counter + 1;
            const reverse = this.props.order < prevProps.order;
            this.setState({
                history: this.state.current ? [
                    ...this.state.history,
                    {
                        ...this.state.current,
                        diappearingReverse: reverse
                    }
                ] : this.state.history,
                current: {
                    counter: counter,
                    children: this.props.children,
                    appearingReverse: reverse
                }
            });
        }
        else if (this.props.children !== prevProps.children) {
            this.setState({
                history: this.state.history,
                current: {
                    ...this.state.current,
                    children: this.props.children,
                }
            });
        }
    }

    removeFromHistory(key) {
        this.setState({
            history: this.state.history.filter(item => item.counter !== key)
        });
    }

    render() {

        const { animate, transition } = this.props;

        if (!animate) return this.props.children;

        // collect animation and transition defintitions
        const firstAnimate = animate[0];
        const secondAnimate = animate[1];
        const thirdAnimate = animate[2] || animate[0];
        const veryFirstAnimate = animate[3] || animate[0];
        const firstTransition = transition && transition[0];
        const secondTransition = transition && (transition[1] || transition[0]);

        // build variants
        const variants = {
            appearing: firstAnimate,
            appearingReverse: thirdAnimate,
            present: applyTransition(secondAnimate, firstTransition),
            veryFirst: applyTransition(veryFirstAnimate, firstTransition),
            disappearing: applyTransition(thirdAnimate, secondTransition),
            disappearingReverse: applyTransition(firstAnimate, secondTransition)
        };

        // render items (inner div as inline-block to avoid margin-collapsing,
        // which may result in leaps when switching from relative to absolute positioning)
        const allItems = this.state.current ? [...this.state.history, this.state.current] : this.state.history;
        return <div style={{position: "relative", display: this.props.inline ? "inline" : "block"}}>
            {allItems.map(
                item => {
                    const isPresent = item.counter === this.state.current.counter;
                    return <motion.div
                        key={item.counter}
                        style={{position: isPresent ? "relative" : "absolute", display: this.props.inline ? "inline" : "inline-block"}}
                        variants={variants}
                        initial={item.veryFirst ? "veryFirst" : item.appearingReverse ? "appearingReverse" : "appearing"}
                        animate={isPresent ? "present" : item.diappearingReverse ? "disappearingReverse" : "disappearing"}
                        onAnimationComplete={() => !isPresent && this.removeFromHistory(item.counter)}
                    >
                        {item.children}
                    </motion.div>;
                }
            )}
        </div>
    }
}

AnimatedUpdate.propTypes = {
    tag: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    animate: PropTypes.array,
    transition: PropTypes.array,
    inline: PropTypes.bool
};